import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import BlockContainer from './BlockContainer';
import Button from '../Button';

const imageType = 'image/png';
const canvasFont = `normal 60px 'Dancing Script'`;

const fonts = [
  'Alex+Brush',
  'Allison',
  'Architects+Daughter',
  'Arizonia',
  'Bad+Script',
  'Calligraffitti',
  'Caveat:wght@400',
  'Cedarville+Cursive',
  'Charm:wght@400',

  'Cookie',
  'Courgette',
  'Covered+By+Your+Grace',
  'Dancing+Script:wght@400',
  'Delius',
  'Gloria+Hallelujah',
  'Great+Vibes',
  'Handlee',
  'Herr+Von+Muellerhoff',
  'Homemade+Apple',
  'Indie+Flower',
  'Italianno',
  'Julee',
  'Kalam:wght@400',
  'Kaushan+Script',
  'Klee+One',
  'Kristi',
  'Macondo',
  'Marck+Script',
  'Merienda:wght@300',
  'Monsieur+La+Doulaise',
  'Mr+Dafoe',
  'Mrs+Saint+Delafield',
  'Ms+Madi',
  'Nanum+Brush+Script',
  'Nanum+Pen+Script',
  'Niconne',
  'Norican',
  'Pacifico',
  'Parisienne',
  'Permanent+Marker',
  'Petit+Formal+Script',
  'Pinyon+Script',
  'Playball',
  'Qwigley',
  'Rancho',
  'Reenie+Beanie',
  'Rochester',
  'Rock+Salt',
  'Sacramento',
  'Satisfy',
  'Seaweed+Script',
  'Shadows+Into+Light+Two',
  'Sofia',
  'Sriracha',
  'Tangerine:wght@400',
  'Yellowtail',
  'Yesteryear',
];

function shuffle(oldArray) {
  const array = [...oldArray];
  let currentIndex = array.length;

  // While there remain elements to shuffle...
  while (currentIndex != 0) {
    // Pick a remaining element...
    let randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }
  return array;
}

const makeFontsForText = (text) => {
  return `https://fonts.googleapis.com/css2?text=${encodeURIComponent(
    text,
  )}&${fonts.map((font) => `family=${font}`).join('&')}`;
};

const handleDownload = (image) => {
  if (!image) return;
  const dlink = document.createElement('a');
  dlink.setAttribute('href', image);
  dlink.setAttribute('download', 'signature.png');
  dlink.click();
};

export default function AISignature({
  title,
  description,
  bgColor,
  textColor,
  padding,
}) {
  const [sigImage, setSigImage] = useState(null);
  const [sigText, setSigText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [copied, setCopied] = useState('');
  const [tab, setTab] = useState('type');

  useEffect(() => {
    if (window.shutdownIntercom) {
      window.shutdownIntercom();
    }
  }, []);

  const sigCanvas = useRef(null);

  const handleSignature = () => {
    const image = sigCanvas.current.getTrimmedCanvas().toDataURL(imageType);
    setSigImage(image);
  };

  const handleClear = () => {
    if (!sigCanvas) return;
    sigCanvas.current.clear();
  };

  const handleText = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = canvasFont;
    const metrics = ctx.measureText(sigText);
    canvas.width = metrics.width;
    canvas.height =
      metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
    ctx.font = canvasFont;
    ctx.fillText(sigText, 0, canvas.height - metrics.fontBoundingBoxDescent);
    const image = canvas.toDataURL(imageType);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    handleDownload(image);
  };

  const handle = (font, copy = false) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `normal 60px '${font.split(':')[0].replace(/\+/g, ' ')}'`;
    const metrics = ctx.measureText(sigText);
    canvas.width = metrics.width;
    canvas.height =
      metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
    ctx.font = `normal 60px '${font.split(':')[0].replace(/\+/g, ' ')}'`;
    ctx.fillText(sigText, 0, canvas.height - metrics.fontBoundingBoxDescent);

    if (copy) {
      canvas.toBlob((blob) => {
        try {
          navigator.clipboard
            .write([
              new ClipboardItem({
                [blob.type]: blob,
              }),
            ])
            .then(() => {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              console.log('Image copied to clipboard');
              setCopied(font);
              setTimeout(() => setCopied(''), 3000);
            })
            .catch((error) => {
              console.error('Failed to copy image to clipboard:', error);
            });
        } catch (error) {
          console.error('Clipboard write operation not supported:', error);
        }
      });
    } else {
      const image = canvas.toDataURL(imageType);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      handleDownload(image);
    }
  };

  const generate = async () => {
    setGenerating(true);
    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = makeFontsForText(sigText);
    document.head.appendChild(stylesheet);
    stylesheet.onload = () => {
      console.log('ready??');
    };
    setTimeout(() => {
      setGenerating(false);
      setGenerated(shuffle(fonts));
    }, Math.random() * 3000 + 1000);
  };

  return (
    <BlockContainer
      bgColor={bgColor}
      textColor={textColor}
      padding={padding}
      title={title}
      description={description}
    >
      <div
        className="text-sm lg:leading-[40px] lg:text-[20px] bg-white inline-block rounded-full mb-6"
        style={{ backgroundColor: 'var(--stone-600)' }}
      >
        <button
          type="button"
          onClick={() => setTab('type')}
          className={`px-10 m-0 rounded-full  transition-all transition-500  py-2 ${
            tab === 'type' ? `bg-midnight  text-white ` : 'pl-8'
          } `}
        >
          Type
        </button>
        <button
          type="button"
          onClick={() => setTab('draw')}
          className={`px-10 m-0 rounded-full transition-all transition-500 py-2 ${
            tab === 'draw' ? `bg-midnight text-white ` : 'pr-8'
          } `}
        >
          Draw
        </button>
      </div>
      <div className="flex flex-col items-center gap-6">
        {tab === 'draw' && (
          <>
            <SignatureCanvas
              ref={sigCanvas}
              canvasProps={{
                className:
                  'bg-white rounded-lg shadow-md w-full max-w-[500px] h-[200px]',
              }}
              onEnd={() => handleSignature()}
            />
            <div className="flex gap-4">
              <Button
                outline
                disabled={!sigImage}
                onClick={() => handleClear()}
              >
                Clear
              </Button>
              <Button
                solid
                yellow
                disabled={!sigImage}
                onClick={() => handleDownload(sigImage)}
              >
                Download
              </Button>
            </div>
          </>
        )}
        {tab === 'type' && (
          <>
            <input
              className="px-6 py-4 text-xl text-center bg-white rounded-lg shadow-md text-black placeholder:text-black/30 w-full max-w-[500px]"
              onChange={(e) => setSigText(e.target.value)}
              placeholder="Your name or initials here"
              type="text"
              disabled={generating || generated}
              name="signature"
              value={sigText}
            />
            <div className="flex flex-col md:flex-row gap-4">
              {tab === 'type' && !generated ? null : (
                <Button
                  outline
                  disabled={!sigText}
                  onClick={() => {
                    setSigText('');
                    setSigImage(null);
                    setGenerated(false);
                    setGenerating(false);
                  }}
                >
                  Clear
                </Button>
              )}
              <Button
                solid
                disabled={!sigText || generating || generated}
                onClick={() => (tab === 'type' ? generate() : handleText())}
              >
                {generated
                  ? 'Done! 🎉'
                  : generating
                  ? 'Generating...'
                  : tab === 'type'
                  ? 'Generate with AI ✨'
                  : 'Download'}
              </Button>
            </div>

            {generated ? (
              <div className="grid grid-cols-2 container gap-3 gap-y-4 sm:grid-cols-3">
                {generated.map((font) => (
                  <div className="rounded-xl p-3 bg-white">
                    <div
                      className="bg-white mb-3  border-2 rounded-md border-stone h-[100px]"
                      style={{
                        fontFamily: `"${font
                          .split(':')[0]
                          .replace(/\+/g, ' ')}", cursive`,
                        fontStyle: 'normal',
                        fontSize: '30px',
                        lineHeight: '100px',
                      }}
                    >
                      {sigText}
                    </div>
                    <Button
                      className="mb-2"
                      sm
                      solid
                      onClick={() => handle(font)}
                    >
                      Download
                    </Button>
                    <Button
                      className="ml-2 mb-2"
                      sm
                      onClick={() => handle(font, true)}
                    >
                      {copied === font ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    </BlockContainer>
  );
}
